import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import WhatsAppButton from "../components/WhatsAppButton";
import { vehicleImage } from "../data/content";
import { useSiteContent } from "../siteContent";

export default function Home() {
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const { company, copy, highlights, services, steps, branchCount, stateCount } = useSiteContent();

  const handleFindBranches = (event) => {
    event.preventDefault();
    const query = city.trim();
    navigate(query ? `/branches?q=${encodeURIComponent(query)}` : "/branches");
  };

  return (
    <>
      <section className="grid min-h-[520px] bg-[#0B6A34] lg:grid-cols-2">
        <div className="flex flex-col justify-center px-5 py-14 sm:px-8 lg:py-20 lg:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] lg:pr-12">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b7e7a8]">
            {company.heroEyebrow}
          </p>
          <h1 className="mt-4 max-w-xl text-4xl font-bold leading-tight text-white sm:text-5xl">
            {company.heroTitle}
            <span className="mt-1 block">{company.heroSubtitle}</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85">{company.heroBody}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/track-shipment" className="rounded-md bg-white px-5 py-3 text-sm font-semibold text-[#0B6A34] hover:bg-[#eef6f0]">
              Track a consignment
            </Link>
            <Link
              to="/service-enquiry"
              className="rounded-md border border-white px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              Request a service
            </Link>
          </div>
          <ul className="mt-6 flex flex-col gap-2 text-sm text-white/90 sm:flex-row sm:flex-wrap sm:gap-x-5">
            {highlights.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Icon name="check" className="h-4 w-4 text-[#b7e7a8]" strokeWidth={2.4} />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative min-h-[280px]">
          <img src={vehicleImage} alt="The Essaar fleet" className="absolute inset-0 h-full w-full object-cover" />
          <p className="absolute bottom-4 left-4 rounded bg-black/55 px-3 py-1 text-sm text-white">The Essaar fleet</p>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="container-page">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6A34]">{copy.services_kicker}</p>
          <h2 className="mt-2 max-w-xl text-3xl font-bold text-[#16301f] sm:text-4xl">{copy.services_title}</h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {services.map((service) => (
              <article key={service.title} className="rounded-xl border border-[#d5e4d9] p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#eef6f0] text-[#0B6A34]">
                  <Icon name={service.icon} className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-[#16301f]">{service.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{service.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f4f7f5] py-16 sm:py-20">
        <div className="container-page">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6A34]">{copy.gallery_kicker}</p>
          <h2 className="mt-2 text-3xl font-bold text-[#16301f] sm:text-4xl">{copy.gallery_title}</h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <figure className="overflow-hidden rounded-xl bg-[#dfe8e2]">
              <img src={vehicleImage} alt="Essaar vehicles at a branch" className="h-full min-h-[280px] w-full object-cover" />
            </figure>
            <div className="grid gap-4">
              <figure className="overflow-hidden rounded-xl">
                <img src={vehicleImage} alt="Essaar fleet lined up for loading" className="h-52 w-full object-cover" />
              </figure>
              <figure className="overflow-hidden rounded-xl">
                <img src={vehicleImage} alt="Essaar vehicle ready for delivery" className="h-52 w-full object-cover" />
              </figure>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="container-page">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6A34]">{copy.steps_kicker}</p>
          <h2 className="mt-2 max-w-xl text-3xl font-bold text-[#16301f] sm:text-4xl">{copy.steps_title}</h2>
          <ol className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <li key={step.step}>
                <p className="text-2xl font-bold text-[#0B6A34]">{step.step}</p>
                <div className="mt-3 h-0.5 w-10 bg-[#6fbf45]" />
                <h3 className="mt-3 text-lg font-bold text-[#16301f]">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#f4f7f5] py-16 sm:py-20">
        <div className="container-page">
          <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6A34]">{copy.branch_kicker}</p>
              <h2 className="mt-2 text-3xl font-bold text-[#16301f] sm:text-4xl">{copy.branch_title}</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600">{copy.branch_body}</p>
              <form onSubmit={handleFindBranches} className="mt-6 flex max-w-lg flex-col gap-2 sm:flex-row">
                <input
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  placeholder="City or pincode"
                  className="w-full rounded-md border border-[#c5d5c9] bg-white px-3 py-2.5 text-sm focus:border-[#0B6A34] focus:outline-none focus:ring-2 focus:ring-[#0B6A34]/20"
                />
                <button type="submit" className="rounded-md bg-[#0B6A34] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#085628]">
                  Find branches
                </button>
              </form>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-xl border border-[#d5e4d9] bg-white p-5">
                <p className="text-3xl font-bold text-[#0B6A34]">{stateCount}</p>
                <p className="mt-1 text-sm text-slate-600">states served</p>
              </div>
              <div className="rounded-xl border border-[#d5e4d9] bg-white p-5">
                <p className="text-3xl font-bold text-[#0B6A34]">{branchCount}</p>
                <p className="mt-1 text-sm text-slate-600">branches</p>
              </div>
              <div className="rounded-xl border border-[#d5e4d9] bg-white p-5 sm:col-span-2 lg:col-span-1">
                <p className="text-3xl font-bold text-[#0B6A34]">{company.yearsInService}</p>
                <p className="mt-1 text-sm text-slate-600">years in service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0B6A34] py-14">
        <div className="container-page grid gap-4 lg:grid-cols-2">
          <article className="rounded-xl border border-white/25 p-6 text-white">
            <h2 className="text-2xl font-bold">{copy.cta_quote_title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/80">{copy.cta_quote_body}</p>
            <Link to="/service-enquiry" className="mt-5 inline-flex rounded-md bg-[#6fbf45] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#5eaa38]">
              Send a service enquiry
            </Link>
          </article>
          <article className="rounded-xl border border-white/25 p-6 text-white">
            <h2 className="text-2xl font-bold">{copy.cta_issue_title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/80">{copy.cta_issue_body}</p>
            <Link to="/complaints" className="mt-5 inline-flex rounded-md border border-white px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10">
              Register a complaint
            </Link>
          </article>
        </div>
      </section>
      <WhatsAppButton number={company.whatsappNumber} label={company.whatsappDisplay} />
    </>
  );
}
